import React, {
  ChangeEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactToPrint from 'react-to-print';
import QRCode from 'qrcode.react';
import cl from 'classnames';
import {
  Account,
  Company,
  Headset,
  PaymentInitiatorEnum,
  WorkingModeEnum,
} from 'src/models';
import { HEADSETS, HOTELS } from 'src/constants';
import {
  usePage,
  useLanguage,
  useContextData,
  usePopUpNotifications,
} from 'src/hooks';
import {
  formatShortDate,
  formatWorkingMode,
  getLastUsedColor,
} from 'src/helper';
import { RoleEnum } from 'src/security/roles';
import { useAppDispatch } from 'src/store';
import { clearReportsHeadsetsState } from 'src/store/reducers/reportHeadsets';
import { useCompanyById } from 'src/services/company';
import {
  useHeadsetById,
  cancelHeadsetActiveSessionAction,
  deleteHeadsetAction,
  deleteCustomerSessionAction,
  saveHeadsetAction,
} from 'src/services/headsets';
import { useAccount } from 'src/services/auth';
import { PageSwitcher, BatteryStatus, CardStatus } from 'src/components';
import {
  Button,
  InputWithLabel,
  SelectWithLabel,
  TextareaWithLabel,
  Modal,
  ClipboardController,
} from 'src/components/general';
import styles from './headsetSettingsStyle.module.scss';

const ContentSection: React.FC<{
  title?: string;
  children?: ReactNode;
  className: string;
}> = ({ title, children, className }) => {
  return (
    <div className={cl(!children && styles.isLoading, className)}>
      {title && <div className={styles.csLabel}>{title}</div>}
      {children || (
        <div className={styles.loading}>
          <div className={styles.loader} />
        </div>
      )}
    </div>
  );
};

const CMK = 'AAABBA';

export const HeadsetSettings = () => {
  const { hotel: companyId, headset: headsetId } = useParams() as {
    hotel: string;
    headset: string;
  };
  const dispatch = useAppDispatch();
  const { t, td } = useLanguage();
  const {
    access: { accessMap },
  } = usePage();
  const {
    navigator: { setBackUrl },
    hcs: { hcsIsVisible, toggleHcsIsVisible },
  } = useContextData();
  const headset = useHeadsetById(headsetId);
  const company = useCompanyById(companyId) as Company;
  const account = useAccount() as Account;
  const [draftHeadset, setDraftHeadset] = useState<Partial<Headset>>({});
  const [showConfirmReset, setShowConfirmReset] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const qrCodeRef = useRef<HTMLDivElement>(null);
  const qrCodeAccRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { addSuccessNotification, addErrorNotification } =
    usePopUpNotifications();

  const [, setCmk] = useState('');

  const cmkClick = useCallback(
    (value: 'A' | 'B') => {
      setCmk((prevState) => {
        const nextState = `${prevState}${value}`;

        if (!CMK.startsWith(nextState)) {
          return '';
        }

        if (CMK === nextState) {
          toggleHcsIsVisible();
          return '';
        }

        return nextState;
      });
    },
    [toggleHcsIsVisible],
  );

  useEffect(() => {
    setDraftHeadset({ ...headset });
  }, [headset]);

  const handleHeadsetUpdate = useCallback(async () => {
    setIsLoading(true);

    const res = await dispatch(
      saveHeadsetAction({
        companyId,
        headsetId,
        data: {
          workingMode: draftHeadset.workingMode ?? WorkingModeEnum.inRoom,
          label: draftHeadset.label,
          roomLabel: draftHeadset.roomLabel,
          isTest: draftHeadset.isTest,
          disabled: draftHeadset.disabled,
          comment: draftHeadset.comment,
        },
      }),
    );

    if (saveHeadsetAction.fulfilled.match(res)) {
      setEditMode(false);
    }

    setIsLoading(false);
  }, [
    dispatch,
    companyId,
    headsetId,
    draftHeadset.workingMode,
    draftHeadset.label,
    draftHeadset.roomLabel,
    draftHeadset.isTest,
    draftHeadset.disabled,
    draftHeadset.comment,
  ]);

  const handleHeadsetCancelSession = useCallback(async () => {
    setIsLoading(true);

    const res = await dispatch(
      cancelHeadsetActiveSessionAction({ companyId, headsetId }),
    );

    if (cancelHeadsetActiveSessionAction.fulfilled.match(res)) {
      setShowConfirmReset(false);
    }

    setIsLoading(false);
  }, [companyId, headsetId, dispatch]);

  const handleHeadsetDelete = useCallback(async () => {
    setIsLoading(true);

    const res = await dispatch(deleteHeadsetAction({ companyId, headsetId }));

    if (deleteHeadsetAction.fulfilled.match(res)) {
      dispatch(clearReportsHeadsetsState());
      setBackUrl(null);
      navigate(`/${HOTELS}/${companyId}/${accessMap[HOTELS][0]}`);
    }

    setIsLoading(false);
  }, [dispatch, companyId, headsetId, navigate, accessMap, setBackUrl]);

  const handleDeleteCustomerSession = useCallback(async () => {
    setIsLoading(true);

    const res = await dispatch(
      deleteCustomerSessionAction({ companyId, headsetId }),
    );

    if (
      deleteCustomerSessionAction.fulfilled.match(res) &&
      res.payload.result
    ) {
      addSuccessNotification('page.headset.hotelCustomerSession.reset.success');
    } else {
      addErrorNotification('page.headset.hotelCustomerSession.reset.failed');
    }

    setIsLoading(false);
  }, [
    companyId,
    headsetId,
    dispatch,
    addSuccessNotification,
    addErrorNotification,
  ]);

  const selectItems = useMemo(
    () => [
      {
        value: true,
        label: t('general.yes'),
      },
      {
        value: false,
        label: t('general.no'),
      },
    ],
    [t],
  );

  const workingModes = useMemo(
    () =>
      [
        WorkingModeEnum.inRoom,
        WorkingModeEnum.vipAccess,
        WorkingModeEnum.reception,
      ].map((value) => ({
        value,
        label: td(formatWorkingMode(value)),
      })),
    [td],
  );

  const qrCode = useMemo(
    () =>
      headset
        ? `${process.env.REACT_APP_WEB_PACKAGES_URL}/${headset.globalId}?initiator=${PaymentInitiatorEnum.qr_code}`
        : '',
    [headset],
  );

  const qrCodeAcc = useMemo(
    () =>
      headset
        ? `${process.env.REACT_APP_WEB_ACCOUNT_URL}/headset/${headset.globalId}`
        : '',
    [headset],
  );

  const isAdmin = useMemo(
    () => account?.role === RoleEnum.admin,
    [account?.role],
  );

  const infoBlock = useMemo(
    () =>
      qrCode && headset ? (
        <div className={cl(styles.infoContentGrid, isAdmin && styles.isAdmin)}>
          <div
            className={cl(styles.qrSectionWrapper, isAdmin && styles.isAdmin)}
          >
            {isAdmin && (
              <div className={styles.topLabel}>
                {t('page.headset.info.paymentPage')}
              </div>
            )}
            <div className={styles.qrSection}>
              <div className={styles.qrContainer}>
                <QRCode
                  value={qrCode}
                  renderAs="canvas"
                  className={styles.qrCode}
                />
                <div className={styles.qrLabel}>
                  {headset?.humanId ?? headset?.label}
                </div>
              </div>
              <div className={styles.qrSectionButtons}>
                <ClipboardController text={qrCode}>
                  <Button
                    label={t('general.copy')}
                    design="lightBlue"
                    size="small"
                    className={styles.copyButton}
                    icon="copy"
                    onClick={() => {
                      cmkClick('A');
                    }}
                  />
                </ClipboardController>
                <ReactToPrint
                  content={() => qrCodeRef.current}
                  trigger={() => (
                    <Button
                      label={t('general.print')}
                      design="blue"
                      size="small"
                      className={styles.printButton}
                      icon="print"
                    />
                  )}
                />
              </div>
            </div>
          </div>
          <div className={cl(styles.infoSection, isAdmin && styles.isAdmin)}>
            {isAdmin && (
              <div className={styles.topLabel}>
                {t('page.headset.info.headsetData')}
              </div>
            )}
            <div className={styles.infoGroup}>
              <div className={styles.infoLabel}>
                {t('page.headset.info.headsetId')}
              </div>
              <div className={styles.infoText}>{headset.phyDeviceId}</div>
            </div>
            <div className={styles.infoGroup}>
              <div className={styles.infoLabel}>
                {t('page.headset.info.globalId')}
              </div>
              <div className={styles.infoText}>{headset.globalId}</div>
            </div>
            <div className={styles.infoGroup}>
              <div className={styles.infoLabel}>
                {t('page.headset.info.humanId')}
              </div>
              <div className={styles.infoText}>{headset.humanId}</div>
            </div>
            <div className={styles.infoGroup}>
              <div className={styles.infoLabel}>
                {t('page.headset.info.deviceType')}
              </div>
              <div className={styles.infoText}>
                {headset.deviceType || t('general.unknown')}
              </div>
            </div>
            <div className={styles.infoGroup}>
              <div className={styles.infoLabel}>
                {t('page.headset.info.firmware')}
              </div>
              <div className={styles.infoText}>
                {headset.status?.firmware || '-'}
              </div>
            </div>
            <div className={styles.infoGroup}>
              <div className={styles.infoLabel}>
                {t('page.headset.info.apMacAddress')}
              </div>
              <div className={styles.infoText}>
                {headset.status?.apMacAddress || '-'}
              </div>
            </div>
            <div className={styles.infoGroup}>
              <div className={styles.infoLabel}>
                {t('page.headset.info.battery')}
              </div>
              <div className={styles.infoText}>
                <BatteryStatus
                  battery={headset.status?.battery}
                  charging={headset.status?.charging}
                  className={styles.tag120}
                />
              </div>
            </div>
            <div className={styles.infoGroup}>
              <div className={styles.infoLabel}>SD</div>
              <div className={styles.infoText}>
                <CardStatus cardState={headset.status?.cardState} />
              </div>
            </div>
            <div className={styles.infoGroup}>
              <div className={styles.infoLabel}>
                {t('page.headset.info.lastUsed')}
              </div>
              <div className={styles.infoText}>
                <div
                  className={cl(
                    styles.tag,
                    styles[getLastUsedColor(headset.lastUsed)],
                  )}
                >
                  {formatShortDate(headset.lastUsed)}
                </div>
              </div>
            </div>
          </div>
          {isAdmin && qrCodeAcc && (
            <div
              className={cl(
                styles.qrSectionWrapper,
                styles.acc,
                isAdmin && styles.isAdmin,
              )}
            >
              {isAdmin && (
                <div className={styles.topLabel}>
                  {t('page.headset.info.personalAccount')}
                </div>
              )}
              <div className={styles.qrSection}>
                <div className={styles.qrContainer}>
                  <QRCode
                    value={qrCodeAcc}
                    renderAs="canvas"
                    className={styles.qrCode}
                  />
                  <div className={styles.qrLabel}>
                    {headset?.humanId ?? headset?.label}
                  </div>
                </div>
                <div className={styles.qrSectionButtons}>
                  <ClipboardController text={qrCodeAcc}>
                    <Button
                      label={t('general.copy')}
                      design="lightBlue"
                      size="small"
                      className={styles.copyButton}
                      icon="copy"
                      onClick={() => {
                        cmkClick('B');
                      }}
                    />
                  </ClipboardController>
                  <ReactToPrint
                    content={() => qrCodeAccRef.current}
                    trigger={() => (
                      <Button
                        label={t('general.print')}
                        design="blue"
                        size="small"
                        className={styles.printButton}
                        icon="print"
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      ) : null,
    [qrCode, headset, isAdmin, t, qrCodeAcc, cmkClick],
  );

  const settingsInfo = useMemo(() => {
    if (!headset) {
      return null;
    }

    if (!editMode) {
      return (
        <div className={styles.settingsSection}>
          <div className={styles.settingsGroup}>
            <div className={styles.infoLabel}>
              {t('page.headset.settings.workingMode')}
            </div>
            <div className={styles.infoText}>
              {td(formatWorkingMode(headset.workingMode))}
            </div>
          </div>
          <div className={styles.settingsGroup}>
            <div className={styles.infoLabel}>
              {t('page.headset.settings.label')}
            </div>
            <div className={styles.infoText}>{headset.label}</div>
          </div>
          <div className={styles.settingsGroup}>
            <div className={styles.infoLabel}>
              {t('page.headset.settings.roomLabel')}
            </div>
            <div className={styles.infoText}>{headset.roomLabel}</div>
          </div>
          <div className={styles.settingsGroup}>
            <div className={styles.infoLabel}>{t('general.test')}</div>
            <div className={styles.infoText}>
              {t(headset.isTest ? 'general.yes' : 'general.no')}
            </div>
          </div>
          <div className={styles.settingsGroup}>
            <div className={styles.infoLabel}>{t('general.blocked')}</div>
            <div className={styles.infoText}>
              {t(headset.disabled ? 'general.yes' : 'general.no')}
            </div>
          </div>
          <div className={styles.settingsGroup}>
            <div className={styles.infoLabel}>
              {t('page.headset.settings.comment')}
            </div>
            <div className={styles.infoText}>{headset.comment}</div>
          </div>
        </div>
      );
    }

    return (
      <>
        <SelectWithLabel
          label={t('page.headset.settings.workingMode')}
          value={draftHeadset.workingMode || WorkingModeEnum.inRoom}
          items={workingModes}
          selectItem={(item) => {
            setDraftHeadset((dh) => ({
              ...dh,
              workingMode: item as WorkingModeEnum,
            }));
          }}
          autoSize
          className={styles.field}
        />
        <InputWithLabel
          label={t('page.headset.settings.label')}
          type="text"
          placeholder={t('page.headset.settings.labelPlaceholder')}
          value={draftHeadset.label || ''}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setDraftHeadset((dh) => ({
              ...dh,
              label: event.target.value,
            }));
          }}
          className={styles.field}
          enterKeyHint="next"
          tabIndex={1}
        />
        <InputWithLabel
          label={t('page.headset.settings.roomLabel')}
          type="text"
          placeholder={t('page.headset.settings.roomLabelPlaceholder')}
          value={draftHeadset.roomLabel || ''}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setDraftHeadset((dh) => ({
              ...dh,
              roomLabel: event.target.value,
            }));
          }}
          className={styles.field}
          enterKeyHint="next"
          tabIndex={2}
        />
        <SelectWithLabel
          label={t('general.test')}
          value={Boolean(draftHeadset.isTest)}
          items={selectItems}
          selectItem={(item) => {
            setDraftHeadset((dh) => ({
              ...dh,
              isTest: item as boolean,
            }));
          }}
          autoSize
          className={styles.field}
        />
        <SelectWithLabel
          label={t('general.blocked')}
          value={Boolean(draftHeadset.disabled)}
          items={selectItems}
          selectItem={(item) => {
            setDraftHeadset((dh) => ({
              ...dh,
              disabled: item as boolean,
            }));
          }}
          autoSize
          className={styles.field}
        />
        <TextareaWithLabel
          label={t('page.headset.settings.comment')}
          placeholder={t('page.headset.settings.commentPlaceholder')}
          value={draftHeadset.comment || ''}
          onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
            setDraftHeadset((dh) => ({
              ...dh,
              comment: event.target.value,
            }));
          }}
          className={styles.field}
          tabIndex={3}
        />
      </>
    );
  }, [
    draftHeadset.disabled,
    draftHeadset.isTest,
    draftHeadset.label,
    draftHeadset.roomLabel,
    draftHeadset.workingMode,
    draftHeadset.comment,
    editMode,
    headset,
    t,
    td,
    selectItems,
    workingModes,
  ]);

  const settingsBlock = useMemo(() => {
    if (!headset) {
      return null;
    }

    return (
      <>
        {settingsInfo}
        <div className={styles.settingsEditButtons}>
          {editMode ? (
            <>
              <Button
                label={t('general.cancel')}
                onClick={() => {
                  setDraftHeadset({ ...headset });
                  setEditMode(false);
                }}
                design="outlineBlue"
                size="small"
                className={styles.settingsButton}
                disabled={isLoading}
              />
              <Button
                label={t('general.save')}
                onClick={() => {
                  handleHeadsetUpdate();
                }}
                design="lightBlue"
                size="small"
                className={styles.settingsButton}
                disabled={isLoading}
              />
            </>
          ) : (
            <Button
              label={t('general.edit')}
              onClick={() => {
                setEditMode(true);
              }}
              design="lightBlue"
              size="small"
            />
          )}
        </div>
        <div className={styles.generalButtonSection}>
          <div className={styles.buttonLabel}>
            {t('page.headset.settings.activeSession')}
          </div>
          <Button
            label={t('general.reset')}
            onClick={() => {
              setShowConfirmReset(true);
            }}
            design="pink"
            size="small"
            disabled={!headset.hasActiveSession}
            className={styles.generalButton}
          />
        </div>
        <div className={styles.generalButtonSection}>
          <div className={styles.buttonLabel}>
            {t('page.headset.settings.deleteHeadset')}
          </div>
          <Button
            label={t('general.delete')}
            onClick={() => {
              setShowConfirmDelete(true);
            }}
            design="pink"
            size="small"
            className={styles.generalButton}
          />
        </div>
      </>
    );
  }, [editMode, isLoading, handleHeadsetUpdate, headset, settingsInfo, t]);

  const customerSessionBlock = useMemo(() => {
    if (!headset) {
      return null;
    }

    if (!headset.hotelCustomerSession) {
      return (
        <div className={styles.infoLabel}>
          {t('page.headset.hotelCustomerSession.noSession')}
        </div>
      );
    }

    return (
      <div className={styles.settingsSection}>
        <div className={styles.settingsGroup}>
          <div className={styles.infoLabel}>
            {t('page.headset.hotelCustomerSession.activatedAt')}
          </div>
          <div className={styles.infoText}>
            {formatShortDate(headset.hotelCustomerSession.activatedAt)}
          </div>
        </div>
        <div className={styles.settingsGroup}>
          <div className={styles.infoLabel}>
            {t('page.headset.hotelCustomerSession.startedAt')}
          </div>
          <div className={styles.infoText}>
            {formatShortDate(headset.hotelCustomerSession.startedAt)}
          </div>
        </div>
        <div className={styles.settingsGroup}>
          <div className={styles.infoLabel}>
            {t('page.headset.hotelCustomerSession.expiredAt')}
          </div>
          <div className={styles.infoText}>
            {formatShortDate(headset.hotelCustomerSession.expiredAt)}
          </div>
        </div>
        <Button
          label={t('general.reset')}
          onClick={() => {
            handleDeleteCustomerSession();
          }}
          design="pink"
          size="small"
          className={styles.generalButton}
          disabled={isLoading}
        />
      </div>
    );
  }, [headset, t, isLoading, handleDeleteCustomerSession]);

  return (
    <div className={styles.container}>
      <Modal
        show={showConfirmReset}
        close={() => {
          setShowConfirmReset(false);
        }}
        header={t('page.headset.settings.activeSession.reset')}
        className={styles.confirmModal}
        footer={
          <div className={styles.confirmModalButtons}>
            <Button
              label={t('general.cancel')}
              onClick={() => {
                setShowConfirmReset(false);
              }}
              disabled={isLoading}
              design="outlineBlue"
              autoSize
              className={cl(styles.modalButton, styles.cancelButton)}
            />
            <Button
              label={t('general.reset')}
              onClick={() => {
                handleHeadsetCancelSession();
              }}
              disabled={isLoading}
              design="pink"
              autoSize
              className={styles.modalButton}
            />
          </div>
        }
        closeOnEsc
      >
        <div className={styles.confirmModalContent}>
          {t('page.headset.settings.activeSession.resetConfirm', {
            headset: headset?.label,
          })}
        </div>
      </Modal>
      <Modal
        show={showConfirmDelete}
        close={() => {
          setShowConfirmDelete(false);
        }}
        header={t('page.headset.settings.deleteHeadset')}
        className={styles.confirmModal}
        footer={
          <div className={styles.confirmModalButtons}>
            <Button
              label={t('general.cancel')}
              onClick={() => {
                setShowConfirmDelete(false);
              }}
              disabled={isLoading}
              design="outlineBlue"
              autoSize
              className={cl(styles.modalButton, styles.cancelButton)}
            />
            <Button
              label={t('general.delete')}
              onClick={() => {
                handleHeadsetDelete();
              }}
              disabled={isLoading}
              design="pink"
              autoSize
              className={styles.modalButton}
            />
          </div>
        }
        closeOnEsc
      >
        <div className={styles.confirmModalContent}>
          {t('page.headset.settings.deleteHeadset.deleteConfirm', {
            headset: headset?.label,
            hotel: company?.name,
          })}
        </div>
      </Modal>
      {Boolean(qrCode && headset) && (
        <div className={styles.printQrHidden}>
          <div ref={qrCodeRef} className={styles.printQrSection}>
            <QRCode
              value={qrCode}
              renderAs="canvas"
              className={styles.printQr}
              width={256}
              height={256}
              style={{
                width: 256,
                height: 256,
              }}
            />
            <div className={styles.printQrLabel}>
              {headset?.humanId ?? headset?.label}
            </div>
          </div>
        </div>
      )}
      {Boolean(qrCodeAcc && headset && isAdmin) && (
        <div className={styles.printQrHidden}>
          <div ref={qrCodeAccRef} className={styles.printQrSection}>
            <QRCode
              value={qrCodeAcc}
              renderAs="canvas"
              className={styles.printQr}
              width={256}
              height={256}
              style={{
                width: 256,
                height: 256,
              }}
            />
            <div className={styles.printQrLabel}>
              {headset?.humanId ?? headset?.label}
            </div>
          </div>
        </div>
      )}
      <div className={styles.topSection}>
        <div
          className={cl(
            styles.pageSwitcherContainer,
            accessMap[HEADSETS].length === 1 && styles.inactive,
          )}
        >
          <PageSwitcher selectClassName={styles.mobileSelect} />
        </div>
      </div>
      <ContentSection className={styles.infoContentSection}>
        {infoBlock}
      </ContentSection>
      <ContentSection
        title={t('page.headset.settings')}
        className={styles.settingsContentSection}
      >
        {settingsBlock}
      </ContentSection>
      {hcsIsVisible && (
        <ContentSection
          title={t('page.headset.hotelCustomerSession')}
          className={styles.customerSessionContentSection}
        >
          {customerSessionBlock}
        </ContentSection>
      )}
    </div>
  );
};

export default HeadsetSettings;
